import { Pencil, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { fmt } from '@/lib/format';
import type { InboundEventDto } from '@performance-crm/shared';
import { useInboundEvents, useInboundSummary, useRetryInboundEvent } from '@/lib/queries';
import { FixRowDialog } from './FixRowDialog';

const TONE = {
  PENDIENTE: 'muted',
  PROCESADO: 'success',
  DUPLICADO: 'warning',
  ERROR: 'danger',
} as const;
const LABEL = {
  PENDIENTE: 'Pendiente',
  PROCESADO: 'Procesado',
  DUPLICADO: 'Recontacto',
  ERROR: 'Error',
} as const;

export function EventsTab({ companyId }: { companyId: string }) {
  const [params, setParams] = useSearchParams();
  // Desde la tabla de importaciones se llega con ?importId=…&status=ERROR para ver solo las filas de esa carga.
  const importId = params.get('importId') ?? undefined;
  const [source, setSource] = useState('');
  const [status, setStatus] = useState(params.get('status') ?? '');
  const { data: summary } = useInboundSummary(companyId);
  const { data, isLoading } = useInboundEvents({ companyId, source, status, importId });
  const clearImport = () => {
    const next = new URLSearchParams(params);
    next.delete('importId');
    next.delete('status');
    setParams(next, { replace: true });
  };
  const retry = useRetryInboundEvent();
  const [open, setOpen] = useState<string | null>(null);
  const [fixing, setFixing] = useState<InboundEventDto | null>(null);
  const apiBase = `${window.location.origin.replace(':5180', ':3610')}`;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-muted-foreground text-[11px] uppercase">Últimas 24 h</p>
            <p className="text-2xl font-semibold">{summary?.last24h ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-muted-foreground text-[11px] uppercase">Errores 24 h</p>
            <p className={`text-2xl font-semibold ${summary?.errors24h ? 'text-danger' : ''}`}>
              {summary?.errors24h ?? '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="md:col-span-2">
          <CardContent className="text-muted-foreground p-4 text-xs">
            <p className="text-foreground font-medium">Cómo enviar leads</p>
            <code className="block truncate">
              POST {apiBase}/api/v1/inbound/&lt;fuente&gt; · cabecera X-API-Key
            </code>
            <p>
              Meta Lead Ads: {apiBase}/api/v1/inbound/meta/webhook · configura las páginas y
              formularios en la empresa.
            </p>
          </CardContent>
        </Card>
      </div>
      {summary && summary.bySource.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.bySource.map((s) => (
            <button
              key={s.source}
              type="button"
              onClick={() => setSource(source === s.source ? '' : s.source)}
              className={`rounded-md border px-3 py-1.5 text-sm ${source === s.source ? 'border-primary bg-primary/10' : 'bg-card'}`}
            >
              <span className="font-medium">{s.source}</span>{' '}
              <span className="text-muted-foreground">{s.total}</span>
              {s.errores > 0 && (
                <Badge tone="danger" className="ml-1">
                  {s.errores} err
                </Badge>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Estado"
          className="w-48"
        >
          <option value="">Todos los estados</option>
          {Object.entries(LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        {importId && (
          <span
            className="bg-card inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
            data-testid="events-import-filter"
          >
            Solo las filas de una importación
            <button
              type="button"
              onClick={clearImport}
              aria-label="Quitar el filtro de importación"
              className="hover:bg-accent rounded p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>
      {isLoading && <Skeleton className="h-48 w-full" />}
      {data && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">Recibido</th>
                <th className="px-3 py-2">Fuente</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Lead</th>
                <th className="px-3 py-2">Detalle</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-t align-top" data-testid="inbound-row">
                  <td className="whitespace-nowrap px-3 py-2">{fmt.dateTime(e.receivedAt)}</td>
                  <td className="px-3 py-2">
                    {e.source}
                    {e.externalId && (
                      <p className="text-muted-foreground text-xs">#{e.externalId}</p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={TONE[e.status]}>{LABEL[e.status]}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    {e.leadId ? (
                      <>
                        <Link to={`/leads/${e.leadId}`} className="hover:underline">
                          {e.leadName ?? 'ver lead'}
                        </Link>
                        {e.doNotContact && (
                          <p>
                            <Badge
                              tone="danger"
                              title="Pidió que no le escribamos: sin WhatsApp ni bot"
                            >
                              no contactar
                            </Badge>
                          </p>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="max-w-md px-3 py-2">
                    {e.error && <p className="text-danger">{e.error}</p>}
                    <button
                      type="button"
                      className="text-primary text-xs underline"
                      onClick={() => setOpen(open === e.id ? null : e.id)}
                    >
                      {open === e.id ? 'ocultar payload' : 'ver payload'}
                    </button>
                    {open === e.id && (
                      <pre className="bg-muted mt-1 max-h-48 overflow-auto rounded p-2 text-[11px]">
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {e.status === 'ERROR' && e.importId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mr-1"
                        onClick={() => setFixing(e)}
                        title="Editar los datos de esta fila y volver a procesarla"
                        data-testid="fix-row"
                      >
                        <Pencil className="h-3 w-3" /> Corregir fila
                      </Button>
                    )}
                    {e.status === 'ERROR' && (
                      <Button size="sm" variant="outline" onClick={() => retry.mutate(e.id)}>
                        <RotateCcw className="h-3 w-3" /> Reintentar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted-foreground px-3 py-8 text-center">
                    Aún no han entrado leads por integraciones. Configura una fuente en "Fuentes y
                    mapeos" o importa un CSV.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {fixing && (
        <FixRowDialog event={fixing} companyId={companyId} onClose={() => setFixing(null)} />
      )}
    </div>
  );
}
